
import { supabase } from '@/integrations/supabase/client';

interface RetryOptions {
  maxRetries?: number;
  baseDelay?: number;
  maxDelay?: number;
}

export const withRetry = async <T>(
  operation: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> => {
  const { maxRetries = 3, baseDelay = 1000, maxDelay = 5000 } = options;
  
  let lastError: Error;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // Check session health before API calls
      if (attempt > 0) {
        console.log(`Retry attempt ${attempt}/${maxRetries}`);
        await validateSession();
      }
      
      return await operation();
    } catch (error: any) {
      lastError = error;
      console.error(`Operation failed on attempt ${attempt + 1}:`, error);
      
      // Don't retry on auth errors or if it's the last attempt
      if (attempt === maxRetries || error?.code === 'PGRST301') {
        break;
      }
      
      // Calculate delay with exponential backoff
      const delay = Math.min(baseDelay * Math.pow(2, attempt), maxDelay);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
};

export const validateSession = async (): Promise<boolean> => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error) {
      console.error('Session validation error:', error);
      return false;
    }
    
    if (!session) {
      console.log('No active session found');
      return false;
    }
    
    // Check if session is expired
    const now = Math.floor(Date.now() / 1000);
    if (session.expires_at && session.expires_at < now) {
      console.log('Session expired, attempting refresh...');
      const { error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError) {
        console.error('Session refresh failed:', refreshError);
        return false;
      }
    }
    
    return true;
  } catch (error) {
    console.error('Session validation failed:', error);
    return false;
  }
};

export const healthySupabaseCall = async <T>(
  operation: () => Promise<T>
): Promise<T> => {
  return withRetry(operation, {
    maxRetries: 3,
    baseDelay: 1000,
    maxDelay: 5000
  });
};
